import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Navigate, generatePath } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import { useTypedParams } from '@sorare/routing';

import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { NBA_PLAY_CHASE_EVENTTYPE_FIXTURE_USERGROUP_USER } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { STORAGE } from '@sorare/core/src/hooks/useLocalStorage';
import { ScarcityType } from '@sorare/core/src/lib/scarcity';

import { getFixtureQueryVariables } from '@sorare/gameplay/src/lib/so5';

import type {
  NBAChasePageQuery,
  NBAChasePageQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const query = gql`
  query NBAChasePageQuery(
    $sport: Sport
    $eventType: So5FixtureEvent
    $type: So5State
    $slug: String
  ) {
    so5 {
      so5Fixture(
        type: $type
        slug: $slug
        sport: $sport
        eventType: $eventType
      ) {
        slug
        gameWeek
        so5Leaderboards {
          slug
          rarityType
          universalSo5UserGroups {
            slug
            rarityType
          }
        }
      }
    }
  }
` as TypedDocumentNode<NBAChasePageQuery, NBAChasePageQueryVariables>;

const PlayChasePageQuery = () => {
  const [preferredScarcity, setPreferredScarcity] =
    useLocalStorage<ScarcityType>(
      STORAGE.mlbMonthlyChaseLastPreferredScarcity,
      'common'
    );
  const { fixture: fixtureSlug, eventType } = useTypedParams<PageParams>();
  const { currentUser } = useCurrentUserContext();

  const userSlug = currentUser?.slug || '';

  const { data } = useQuery(query, {
    variables: {
      ...getFixtureQueryVariables(fixtureSlug),
      eventType: eventType.toUpperCase() as So5FixtureEvent,
      sport: Sport.NBA,
    },
  });

  const chaseLeaderboards =
    data?.so5.so5Fixture?.so5Leaderboards.filter(
      l => l.universalSo5UserGroups.length > 0
    ) ?? [];

  const userGroupSlug = chaseLeaderboards?.find(({ rarityType }) => {
    return rarityType === preferredScarcity;
  })?.universalSo5UserGroups[0]?.slug;

  if ((data && chaseLeaderboards.length === 0) || !userSlug) {
    return (
      <div>
        <FormattedMessage
          id="Play.chase.noChaseLeaderboardFound"
          defaultMessage="No chase leaderboard found"
        />
      </div>
    );
  }

  if (chaseLeaderboards.length > 0 && !userGroupSlug) {
    setPreferredScarcity(chaseLeaderboards[0]?.rarityType as ScarcityType);
  }

  // chase leaderboard found, redirect to Chase competition page
  if (userGroupSlug) {
    return (
      <Navigate
        to={generatePath(NBA_PLAY_CHASE_EVENTTYPE_FIXTURE_USERGROUP_USER, {
          fixture: fixtureSlug,
          userGroup: userGroupSlug,
          user: userSlug,
          eventType,
        })}
        replace
      />
    );
  }

  return null;
};

export default PlayChasePageQuery;
